
import { supabase } from '@/integrations/supabase/client';
import { MutableRefObject } from 'react';

interface AdminAccessCheckProps {
  setIsAuthenticated: (value: boolean) => void;
  setUserRole: (value: 'admin' | 'gestionnaire' | 'assistant' | null) => void;
  setUserName: (value: string | null) => void;
  setIsLoading: (value: boolean) => void;
  authCheckCompletedRef: MutableRefObject<boolean>;
  toast: any;
}

export const useAdminAccessCheck = ({
  setIsAuthenticated,
  setUserRole,
  setUserName,
  setIsLoading,
  authCheckCompletedRef,
  toast
}: AdminAccessCheckProps) => {
  // Vérification optimisée, utilisation d'un cache local pour éviter les requêtes répétées
  const checkAdminAccess = async (userId: string) => {
    try {
      // Vérifions d'abord dans le localStorage pour une vérification rapide
      const cachedRole = sessionStorage.getItem('admin_role');
      const cachedName = sessionStorage.getItem('admin_name');
      const cacheTimestamp = sessionStorage.getItem('admin_cache_timestamp');
      const now = Date.now();
      const cacheValid = cacheTimestamp && (now - parseInt(cacheTimestamp) < 60 * 1000); // Cache valide pour 1 minute
      
      // Si nous avons un cache valide, utilisons-le pour un premier rendu rapide
      if (cacheValid && cachedRole) {
        console.log("Utilisation des données en cache pour l'accès administrateur");
        setIsAuthenticated(true);
        setUserRole(cachedRole as 'admin' | 'gestionnaire' | 'assistant');
        setUserName(cachedName);
        // Le cache RÉPOND : la vérification est terminée. Ces deux lignes manquaient —
        // isLoading restait à true jusqu'à la fin du contrôle de fond, si bien que le
        // « rendu rapide » que le cache est censé permettre affichait quand même un
        // squelette. Elles rendent aussi la vérification suivante silencieuse.
        setIsLoading(false);
        authCheckCompletedRef.current = true;

        // Même avec le cache, continuons à vérifier en arrière-plan
        setTimeout(() => verifyAdminRights(userId), 0);
        return;
      }
      
      // Pas de cache, effectuer une vérification complète
      await verifyAdminRights(userId);
      
    } catch (error) {
      console.error("Exception lors de la vérification de l'accès:", error);
      // Une revérification en arrière-plan qui échoue (réseau, verrou) ne dit PAS que
      // l'utilisateur a perdu ses droits : elle dit qu'on ne sait pas. Éjecter sur cette
      // base ferait disparaître l'écran ouvert d'un admin parfaitement légitime.
      if (authCheckCompletedRef.current) return;
      setIsAuthenticated(false);
      setUserRole(null);
      authCheckCompletedRef.current = true;
      setIsLoading(false);
    }
  };

  // Fonction séparée pour la vérification complète
  const verifyAdminRights = async (userId: string) => {
    // NE JAMAIS REPASSER EN CHARGEMENT une fois l'accès confirmé. RequireAdmin démonte
    // ses enfants tant que isLoading vaut true : un aller-retour réseau de 200 ms
    // suffisait à détruire l'écran admin ouvert et l'état d'un devis en cours de saisie.
    // Une revérification est donc SILENCIEUSE — elle corrige l'état si les droits ont
    // réellement changé, sans jamais interrompre le travail en cours.
    const premiereVerification = !authCheckCompletedRef.current;
    if (premiereVerification) setIsLoading(true);

    try {
      await verifierDroits(userId, premiereVerification);
    } finally {
      if (premiereVerification) setIsLoading(false);
    }
  };

  // Corps de la vérification. `premiereVerification` distingue le chargement initial
  // — où l'on peut conclure « non autorisé » — d'une revalidation de confort.
  const verifierDroits = async (userId: string, premiereVerification: boolean) => {
    console.log("Vérification de l'accès administrateur...");

    // Appel unique pour récupérer les informations de l'utilisateur
    const { data: adminUser, error: adminUserError } = await supabase
      .from('admin_users')
      .select('role, name, firstname, lastname, email')
      .eq('id', userId)
      .maybeSingle();
        
    console.log("Résultat requête admin_users:", { adminUser, adminUserError });
    
    if (adminUserError) {
      console.error("Erreur lors de la récupération des infos admin:", adminUserError);
      // UNE ERREUR N'EST PAS UNE ABSENCE. Sans ce garde-fou, un incident réseau vidait
      // `adminUser`, la suite en concluait « absent de admin_users » et enchaînait sur
      // le repli RPC — jusqu'à déconnecter un admin légitime. Sur une revérification en
      // arrière-plan, on ne conclut rien : on garde l'état et on retentera plus tard.
      if (!premiereVerification) return;
    }

    // Si l'utilisateur est trouvé dans admin_users
    if (adminUser) {
      console.log(`Utilisateur admin trouvé dans la table, rôle: ${adminUser.role || 'admin'}`);
      setIsAuthenticated(true);
      
      // Si pas de rôle spécifié, considéré comme admin par défaut
      const role = adminUser.role || 'admin';
      setUserRole(role);
      
      const name = adminUser.name || adminUser.firstname || adminUser.email;
      setUserName(name);
      
      // Mise en cache des informations pour les prochains chargements
      sessionStorage.setItem('admin_role', role);
      sessionStorage.setItem('admin_name', name || '');
      sessionStorage.setItem('admin_cache_timestamp', Date.now().toString());
      
      authCheckCompletedRef.current = true;
    } else {
      console.warn("Utilisateur présent dans Auth mais pas dans admin_users");
      
      // Vérification par fonction RPC is_admin
      try {
        const { data: isAdmin, error: isAdminError } = await supabase.rpc(
          'is_admin', 
          { user_id: userId }
        );

        console.log("Résultat de la vérification is_admin:", { isAdmin, isAdminError });

        if (isAdminError) {
          console.error("Erreur lors de la vérification des droits administrateur:", isAdminError);
          // Là encore : une erreur dit « on ne sait pas », pas « pas admin ». Le
          // signOut() qui figurait ici détruisait la SESSION d'un administrateur
          // légitime sur un simple incident réseau, sans rien avoir prouvé. Seule une
          // réponse négative certaine (plus bas) justifie une déconnexion.
          if (!premiereVerification) return;
          toast({
            title: "Erreur d'authentification",
            description: "Impossible de vérifier vos droits d'accès. Veuillez réessayer.",
            variant: "destructive"
          });

          setIsAuthenticated(false);
          authCheckCompletedRef.current = true;
          return;
        }

        if (isAdmin) {
          console.log("L'utilisateur est reconnu comme admin par la fonction RPC");
          setIsAuthenticated(true);
          setUserRole('admin');
          sessionStorage.setItem('admin_role', 'admin');
          sessionStorage.setItem('admin_cache_timestamp', Date.now().toString());
          authCheckCompletedRef.current = true;
          // NB : on N'INSÈRE PLUS dans admin_users depuis le client (écriture d'un
          // rôle privilégié codé en dur). L'attribution des droits doit se faire
          // côté serveur (RPC SECURITY DEFINER / dashboard). Cette branche n'est
          // atteinte que si is_admin() (qui lit admin_users) renvoie déjà true.
        } else {
          console.log("L'utilisateur n'a pas les droits d'administrateur");
          toast({
            title: "Accès refusé",
            description: "Votre compte n'a pas les autorisations nécessaires pour accéder à l'administration.",
            variant: "destructive"
          });
          
          await supabase.auth.signOut();
          setIsAuthenticated(false);
          authCheckCompletedRef.current = true;
        }
      } catch (error) {
        console.error("Erreur lors de la vérification RPC:", error);
        if (!premiereVerification) return;
        setIsAuthenticated(false);
        setUserRole(null);
        authCheckCompletedRef.current = true;
      }
    }
    // setIsLoading(false) vit désormais dans le `finally` de verifyAdminRights : il
    // était ici, après une série de `return` qui le sautaient. Un seul chemin d'erreur
    // suffisait à laisser isLoading à true pour toujours.
  };

  return { checkAdminAccess };
};
